<template>
  <v-dialog
    :model-value="modelValue"
    max-width="960"
    persistent
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card rounded="lg" data-cy="disb-send-dialog">
      <v-card-title class="d-flex align-center justify-space-between pt-5 px-6">
        <span class="text-h6 font-weight-bold">{{ resendFrom ? 'Resend Deposit Request Email' : 'Send Deposit Request' }}</span>
        <LlSecondaryButton
          icon="mdi-close"
          variant="text"
          size="small"
          aria-label="Close"
          :disabled="sending"
          data-cy="disb-send-close"
          @click="close"
        />
      </v-card-title>

      <v-card-text class="px-6 pb-2">
        <Loader v-if="loadingContact" :what="'recipient'" class="my-12" />

        <div v-else class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <!-- Composition -->
          <v-form ref="formRef">
            <fieldset class="border border-dashed border-gray-300 rounded px-3 pt-2 pb-3 mb-4">
              <legend class="text-xs uppercase tracking-wider text-action-grey px-1">
                Recipient on file — editable
              </legend>
              <LlTextField
                v-model="form.clientName"
                label="Full name"
                :rules="[requiredRule('A recipient name is required.')]"
                data-cy="disb-send-name"
              />
              <LlTextarea
                v-model="form.mailingAddress"
                label="Mailing address"
                :rows="3"
                data-cy="disb-send-address"
              />
            </fieldset>

            <LlTextField
              v-model="form.to"
              label="To"
              :rules="[requiredRule('A recipient email address is required.'), toRule]"
              data-cy="disb-send-to"
            />
            <!-- The recipient decides which bank account the disbursement lands in, so an
                 address that isn't the one on file is worth showing plainly before sending. -->
            <div
              v-if="toDiverges"
              class="text-xs text-error mb-2"
              data-cy="disb-send-to-diverges"
            >
              This differs from {{ onFileEmailSource }} ({{ onFileEmail }}). The deposit link
              will go to the address above, for this send only — the client record is unchanged.
            </div>

            <div class="grid grid-cols-2 gap-4">
              <div>
                <LlTextField
                  v-model="form.ccs"
                  label="Cc"
                  :rules="[optionalEmailsRule]"
                  data-cy="disb-send-cc"
                />
              </div>
              <div>
                <LlTextField
                  v-model="form.bccs"
                  label="Bcc"
                  :rules="[optionalEmailsRule]"
                  data-cy="disb-send-bcc"
                />
              </div>
            </div>

            <LlTextField
              v-model="form.subject"
              label="Subject"
              :rules="[requiredRule('A subject is required.'), subjectRule]"
              data-cy="disb-send-subject"
            />

            <LlTextarea
              v-model="form.body"
              label="Message"
              :rows="5"
              :rules="[requiredRule('A message is required.')]"
              data-cy="disb-send-body"
            />
          </v-form>

          <!-- Preview -->
          <div>
            <div class="text-xs uppercase tracking-wider text-action-grey mb-2">Preview</div>
            <div class="border border-gray-200 rounded bg-white p-5" data-cy="disb-send-preview">
              <div class="font-bold mb-3">{{ form.subject }}</div>
              <p class="text-sm mb-3">{{ greeting }}</p>
              <!-- ll-standards-ignore LL091 -- derived from the body on each render; never reordered -->
              <p
                v-for="(paragraph, index) in previewParagraphs"
                :key="index"
                class="text-sm mb-3 whitespace-pre-line"
              >
                {{ paragraph }}
              </p>
              <!-- green.regular, hardcoded as #29bf12 in PayabliDisbursementEmailService.CtaButtonColor
                   too: the sent button is inline-styled HTML and cannot read this class. -->
              <div
                class="inline-block bg-green-regular text-white text-sm font-semibold rounded px-4 py-2 mt-1 mb-3"
                data-cy="disb-send-preview-cta"
              >
                {{ ctaLabel }}
              </div>
              <div class="text-xs text-action-grey">
                Link expires in {{ expiryDays }} days · sent to {{ form.to || '—' }}
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="amountDrifted"
          class="text-sm text-error mt-4"
          data-cy="disb-send-amount-drift"
        >
          The client's net recovery changed to {{ formatCurrency(amount) }} while this was open.
          Update the message before sending — the payment uses the new figure either way.
        </div>

        <!-- The store also records this against the client row, but that row is behind this
             overlay — without it here a refused send looks like nothing happened. `attempted`
             scopes it to this opening, so a stale failure doesn't greet the next one. -->
        <div
          v-if="attempted && errorMessage && !loadingContact"
          class="text-sm text-error mt-4"
          data-cy="disb-send-error"
        >
          {{ errorMessage }}
        </div>
      </v-card-text>

      <v-card-actions class="px-6 pb-5 pt-2 justify-end gap-2">
        <LlSecondaryButton
          :disabled="sending"
          data-cy="disb-send-cancel"
          @click="close"
        >
          Cancel
        </LlSecondaryButton>
        <LlSecondaryButton
          color="primary"
          variant="elevated"
          :loading="sending"
          :disabled="!canSend"
          data-cy="disb-send-confirm"
          @click="confirm"
        >
          {{ resendFrom ? 'Resend' : 'Send' }}
        </LlSecondaryButton>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue';
import LlTextField from '@/components/LlTextField.vue';
import LlTextarea from '@/components/LlTextarea.vue';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import Loader from '@/components/Loader.vue';
import { formatCurrency, isValidEmail, isValidSingleEmail } from '@/services/utils';
import useClientMatterStore from '@/services/useClientMatterStore';
import type {
  DisbursementEmailComposition,
  SendClientDisbursementPayload,
} from '@/models/disbursementTypes';
import {
  DISBURSEMENT_CTA_LABEL,
  LINK_EXPIRY_DAYS,
  buildDefaultBody,
  buildDefaultSubject,
  buildGreeting,
  formatContactAddress,
  fromEmailHtml,
  hasHeaderInjectionRisk,
  normalizeRecipients,
  toEmailHtml,
} from '@/utils/payabliDisbursementEmail';

const props = defineProps<{
  modelValue: boolean;
  matterId: string;
  clientName: string;
  amount: number;
  sending: boolean;
  errorMessage: string | null;
  /** Resend mode (sc-47502): pre-fill from the last composition rather than the matter contact. */
  resendFrom?: DisbursementEmailComposition | null;
}>();

const emit = defineEmits<{
  'update:modelValue': [boolean];
  submit: [SendClientDisbursementPayload];
}>();

const SUBJECT_MAX = 255;

const ctaLabel = DISBURSEMENT_CTA_LABEL;
const expiryDays = LINK_EXPIRY_DAYS;

const clientMatterStore = useClientMatterStore();
const formRef = ref<{ validate: () => Promise<{ valid: boolean }> } | null>(null);
const loadingContact = ref(false);
const attempted = ref(false);
const onFileEmail = ref('');
const onFileEmailFromMatter = ref(false);
const composedAmount = ref(0);
const form = reactive({
  clientName: '',
  mailingAddress: '',
  to: '',
  ccs: '',
  bccs: '',
  subject: '',
  body: '',
});

const requiredRule = (message: string) => (value: string): true | string =>
  !!value?.trim() || message;

const toRule = (value: string): true | string =>
  !value?.trim() || isValidSingleEmail(value.trim()) || 'Enter a valid email address.';

const optionalEmailsRule = (value: string): true | string =>
  !value?.trim() || isValidEmail(normalizeRecipients(value)) || 'Enter valid, comma-separated email addresses.';

const subjectRule = (value: string): true | string => {
  if (hasHeaderInjectionRisk(value ?? '')) return 'The subject cannot contain line breaks.';
  return (value ?? '').length <= SUBJECT_MAX || `Keep the subject under ${SUBJECT_MAX} characters.`;
};

// Mirrors the :rules above for environments without v-form, the same way LienFormPanel does.
const canSend = computed(
  () =>
    !props.sending
    && !loadingContact.value
    && !!form.clientName.trim()
    && !!form.body.trim()
    && requiredRule('')(form.subject) === true
    && subjectRule(form.subject) === true
    && !!form.to.trim()
    && toRule(form.to) === true
    && optionalEmailsRule(form.ccs) === true
    && optionalEmailsRule(form.bccs) === true,
);

const onFileEmailSource = computed(() =>
  (onFileEmailFromMatter.value
    ? 'the contact recorded on the matter'
    : "the client's contact email"),
);

const toDiverges = computed(
  () =>
    !!onFileEmail.value
    && !!form.to.trim()
    && toRule(form.to) === true
    && form.to.trim().toLowerCase() !== onFileEmail.value.toLowerCase(),
);

// A resend carries the amount already sent; the live net-recovery figure is irrelevant to it.
const amountDrifted = computed(
  () => !props.resendFrom && !loadingContact.value && props.amount !== composedAmount.value,
);

const greeting = computed(() => buildGreeting(form.clientName || props.clientName));
const previewParagraphs = computed(() =>
  form.body.split(/\n\s*\n/).map((block) => block.trim()).filter(Boolean),
);

/**
 * Mirrors the backend's BR-011 resolution (Matter.Email ?? Client.Email) so the address shown
 * here is the same one the Send gate checked — a different answer would let the modal open
 * pre-filled with an address the gate never approved.
 */
function applyContact(matter: any): void {
  const matterContact = matter?.contact ?? {};
  const clientContact = matter?.client?.contact ?? {};

  // ClientService regenerates client.name as DisplayName -> CompanyName -> "Last, First" on every
  // save, so it already IS the precedence chain — re-deriving it here would be a second copy that
  // drifts. It is also what invoice and trust-request emails greet the client by ({client_name}),
  // so this keeps one client addressed the same way across every email LeanLaw sends.
  form.clientName = matter?.client?.name || props.clientName;
  form.mailingAddress = formatContactAddress(clientContact.address);
  if (matter?.name) form.subject = buildDefaultSubject(matter.name);

  // Whichever contact wins supplies the cc/bcc too. Taking the matter's address but the client's
  // cc would copy someone the matter-level contact was written to exclude — and this email
  // carries the client's settlement figures.
  onFileEmailFromMatter.value = !!matterContact.email;
  const source = onFileEmailFromMatter.value ? matterContact : clientContact;
  onFileEmail.value = source.email || '';
  form.to = onFileEmail.value;
  form.ccs = normalizeRecipients(source.cc || '');
  form.bccs = normalizeRecipients(source.bcc || '');
}

function applyResendFrom(previous: DisbursementEmailComposition): void {
  attempted.value = false;
  composedAmount.value = props.amount;
  onFileEmail.value = '';
  onFileEmailFromMatter.value = false;
  form.clientName = previous.clientName || props.clientName;
  form.mailingAddress = previous.mailingAddress;
  form.to = previous.to;
  form.ccs = normalizeRecipients(previous.ccs);
  form.bccs = normalizeRecipients(previous.bccs);
  form.subject = previous.subject;
  form.body = fromEmailHtml(previous.body);
}

async function loadRecipient(): Promise<void> {
  if (props.resendFrom) {
    applyResendFrom(props.resendFrom);
    return;
  }
  loadingContact.value = true;
  attempted.value = false;
  composedAmount.value = props.amount;
  onFileEmail.value = '';
  onFileEmailFromMatter.value = false;
  form.clientName = props.clientName;
  form.mailingAddress = '';
  form.to = '';
  form.ccs = '';
  form.bccs = '';
  // Filled in properly from the matter once it loads; the settlement read carries no name.
  form.subject = buildDefaultSubject('');
  form.body = buildDefaultBody(props.amount);

  try {
    // force: the contact is the one thing this modal must not serve from an earlier page's cache.
    applyContact(await clientMatterStore.getMatterById(props.matterId, true));
  } catch {
    // The defaults above still stand and every field is editable, so a failed read costs the
    // firm the pre-fill, not the send.
  } finally {
    loadingContact.value = false;
  }
}

watch(
  () => props.modelValue,
  (open) => {
    if (open) loadRecipient();
  },
  { immediate: true },
);

function close(): void {
  emit('update:modelValue', false);
}

async function confirm(): Promise<void> {
  if (!canSend.value) return;
  attempted.value = true;
  try {
    const result = await formRef.value?.validate?.();
    if (result && !result.valid) return;
  } catch {
    // canSend already mirrors every rule above, so a v-form that fails to run is not itself a
    // reason to refuse a composition that has otherwise validated.
  }

  emit('submit', {
    clientName: form.clientName.trim(),
    mailingAddress: form.mailingAddress.trim(),
    to: form.to.trim(),
    ccs: normalizeRecipients(form.ccs),
    bccs: normalizeRecipients(form.bccs),
    subject: form.subject.trim(),
    body: toEmailHtml(form.body),
  });
}
</script>
